import { action, makeAutoObservable, reaction, runInAction, set } from 'mobx';
import type { IReactionDisposer } from 'mobx';
import { throttle } from 'throttle-debounce';

import { WELL_DIRECTORY_SERVICE_API_URL } from '../../shared/api/constants';
import { CurvesCacheService } from '../../shared/api/CurvesCacheService';
import { DashboardService } from '../../shared/api/DashboardService';
import type { Wellbore } from '../../shared/api/IDashboardService';
import type { WellDto, WellIndexType } from '../../shared/api/IWellDirectoryService';
import { WellDirectoryService } from '../../shared/api/WellDirectoryService';
import { BaseParameterTypes } from '../../shared/baseParams';
import { getRandomCurveColor } from '../../shared/curvePalette';
import type { WellLogWidgetStompPublisher } from '../../shared/stompTypes';
import { Wellbores } from '../../shared/stores/WellboresStore';
import { WellLogs } from '../../shared/stores/WellLogs';
import type {
  DirectionalDrillingAppParams,
  DirectionalDrillingEvents,
  DirectionalDrillingInitParams,
  DirectionalDrillingStateParams,
} from '../types';

import { DirectionalDrillingStatePublisher } from './DirectionalDrillingStatePublisher';
import { ToolState } from './ToolState';

export class DirectionalDrillingState {
  wellId: number;
  isBaseParamsLoading = false;
  isLoading = false;
  appParams: DirectionalDrillingAppParams;
  stompPublisher: WellLogWidgetStompPublisher;
  wellDirectoryService: WellDirectoryService;
  statePublisher: DirectionalDrillingStatePublisher;
  wellData: WellDto | null = null;
  logs: WellLogs;
  wellbores: Wellbores;
  eventHandlers: DirectionalDrillingEvents;
  index: WellIndexType = 'LOG_TIME';
  curvesCacheService: CurvesCacheService;
  dashboardService: DashboardService;
  gravityToolState: ToolState | null = null;
  magneticToolState: ToolState | null = null;
  isPaused = false;
  is360 = false;
  baseParamsIssue: 'noBaseParams' | 'unsupportedUnits' | 'curvesUnavailable' | null = null;
  error: Error | null = null;

  private throttledInit: throttle<() => void>;
  private abortController: AbortController | null = null;
  private disposerUnsub: IReactionDisposer;
  private unsubTimeout: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(
    initParams: DirectionalDrillingInitParams,
    appParams: DirectionalDrillingAppParams,
    initState: Partial<DirectionalDrillingStateParams>,
    eventHandlers: DirectionalDrillingEvents,
  ) {
    makeAutoObservable<this, 'throttledInit'>(this, { init: action.bound, throttledInit: false });
    this.stompPublisher = initParams.stompPublisher;
    this.appParams = appParams;
    this.wellId = appParams.wellId;
    this.eventHandlers = eventHandlers;
    this.curvesCacheService = new CurvesCacheService(initParams.legacyAxiosInstance);
    this.wellDirectoryService = new WellDirectoryService(initParams.axiosInstance, WELL_DIRECTORY_SERVICE_API_URL);
    this.logs = new WellLogs(this.wellDirectoryService, initParams.wellDirectoryServiceStompPublisher);
    this.dashboardService = new DashboardService(initParams.axiosInstance);
    this.wellbores = new Wellbores(this.dashboardService, (wellbore) => {
      this.eventHandlers.onWellboreChanged?.(wellbore.specialName);
    });
    this.statePublisher = new DirectionalDrillingStatePublisher(initState, eventHandlers.onInternalStateChanged);
    this.isPaused = initState.isPaused ?? false;
    this.is360 = initState.is360 ?? false;
    this.throttledInit = throttle(800, this.init);
    this.disposerUnsub = reaction(
      () => this.appParams.active,
      (active) => {
        this.clearSubTimeout();
        if (active === false) {
          this.unsubTimeout = setTimeout(() => {
            this.unsubTools();
            this.clearSubTimeout();
          }, 30_000);
        } else {
          this.subTools();
        }
      },
      { fireImmediately: true },
    );
    void this.init();
  }

  get isSuppressTooltip() {
    return this.appParams.resizing === true || this.appParams.moving === true || this.appParams.inFocus === false;
  }

  get noBaseParams() {
    return this.baseParamsIssue === 'noBaseParams';
  }

  get fullyLoaded() {
    return !this.isLoading && !this.isBaseParamsLoading;
  }

  async init() {
    if (this.destroyed) {
      return;
    }
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.isLoading = true;
    this.isBaseParamsLoading = false;
    this.error = null;
    this.baseParamsIssue = null;
    this.destroyTools();
    this.logs.clear();
    this.wellbores.clear();

    try {
      const wellData = await this.wellDirectoryService.getWell(this.wellId, controller);
      controller.signal.throwIfAborted();
      runInAction(() => {
        this.wellData = wellData;
      });
      await this.wellbores.fetchWellbores(this.wellId, controller);
      controller.signal.throwIfAborted();
      const selected = this.wellbores.getDefaultSelected(
        this.appParams.defaultSelectWellbore === 'active',
        this.statePublisher.params.wellboreId,
      );
      if (selected) {
        await this.loadWellbore(selected, controller);
      } else {
        runInAction(() => {
          this.baseParamsIssue = 'noBaseParams';
        });
      }
      controller.signal.throwIfAborted();
      this.eventHandlers.onInitializedSuccess?.();
    } catch (error) {
      if (!controller.signal.aborted) {
        this.reportError(error);
      }
    } finally {
      if (!controller.signal.aborted) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  }

  private async loadWellbore(wellbore: Wellbore, controller: AbortController) {
    this.destroyTools();
    this.logs.clear();
    this.wellbores.setSelected(wellbore);
    await this.logs.fetchWellLogs(this.wellId, wellbore.id, this.index, controller);
    controller.signal.throwIfAborted();
    const baseParams = await this.wellDirectoryService.getBaseParameters(
      wellbore.id,
      this.index,
      undefined,
      controller,
    );
    controller.signal.throwIfAborted();
    const gravityColor = getRandomCurveColor();
    const magneticColor = getRandomCurveColor([gravityColor]);
    let baseParamsIssue: typeof this.baseParamsIssue = 'noBaseParams';

    for (const type of ['gravity', 'magnetic'] as const) {
      const parameter = type === 'gravity' ? BaseParameterTypes.GRAVITY_TOOLFACE : BaseParameterTypes.MAGNETIC_TOOLFACE;
      const curveId = baseParams.find((param) => param.parameter === parameter)?.curveId;
      if (!curveId) {
        continue;
      }
      const curve = this.logs.allCurves.find((item) => item.goStreamId === curveId);
      if (!curve) {
        baseParamsIssue = 'curvesUnavailable';
        continue;
      }
      if (curve.unit.abbreviation !== 'dega' && curve.unit.abbreviation !== 'rad') {
        if (baseParamsIssue !== 'curvesUnavailable') {
          baseParamsIssue = 'unsupportedUnits';
        }
        continue;
      }
      const data = await this.curvesCacheService.getCurve(curveId, undefined, controller);
      controller.signal.throwIfAborted();
      runInAction(() => {
        const tool = new ToolState(
          data,
          curve,
          type,
          this.appParams.isAltNames,
          this.statePublisher,
          (type === 'gravity'
            ? this.statePublisher.params.gravityToolParams
            : this.statePublisher.params.magneticToolParams) ?? null,
          type === 'gravity' ? gravityColor : magneticColor,
          this.stompPublisher,
          this.isPaused || this.appParams.active === false,
          this.curvesCacheService,
        );
        if (type === 'gravity') {
          this.gravityToolState = tool;
        } else {
          this.magneticToolState = tool;
        }
      });
    }
    runInAction(() => {
      this.baseParamsIssue = this.gravityToolState || this.magneticToolState ? null : baseParamsIssue;
      this.statePublisher.setParams({ wellboreId: wellbore.id });
    });
  }

  async switchWellbore(wellboreId: number) {
    const wellbore = this.wellbores.getWellboreById(wellboreId);
    if (!wellbore || this.destroyed || this.isLoading) {
      return;
    }
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.isBaseParamsLoading = true;
    this.baseParamsIssue = null;
    this.error = null;
    try {
      await this.loadWellbore(wellbore, controller);
    } catch (error) {
      if (!controller.signal.aborted) {
        this.reportError(error);
      }
    } finally {
      if (!controller.signal.aborted) {
        runInAction(() => {
          this.isBaseParamsLoading = false;
        });
      }
    }
  }

  private reportError(error: unknown) {
    this.error = error instanceof Error ? error : new Error(String(error));
    this.destroyTools();
    this.eventHandlers.onInitializationFailed?.(this.error.message, this.error);
  }

  setAppParams(params: Partial<DirectionalDrillingAppParams>) {
    set(this.appParams, params);
    if (params.locale && this.wellbores.selected) {
      this.eventHandlers.onWellboreChanged?.(this.wellbores.selected.specialName);
    }
    if (params.isAltNames != null) {
      this.gravityToolState?.setAltNames(params.isAltNames);
      this.magneticToolState?.setAltNames(params.isAltNames);
    }
    if (params.wellId != null && params.wellId !== this.wellId) {
      this.abortController?.abort();
      this.destroyTools();
      this.isLoading = true;
      this.wellId = params.wellId;
      this.throttledInit();
    }
  }

  saveSettings(is360: boolean) {
    this.is360 = is360;
    this.statePublisher.setParams({ is360 });
    this.gravityToolState?.saveTemporaryData();
    this.magneticToolState?.saveTemporaryData();
  }

  toggleIsPaused() {
    this.isPaused = !this.isPaused;
    this.statePublisher.setParams({ isPaused: this.isPaused });
    if (this.isPaused) {
      this.unsubTools();
    } else {
      this.subTools();
    }
  }

  private clearSubTimeout() {
    if (this.unsubTimeout) {
      clearTimeout(this.unsubTimeout);
      this.unsubTimeout = null;
    }
  }

  private subTools() {
    if (!this.isPaused && !this.destroyed && this.appParams.active !== false) {
      this.gravityToolState?.subscribeSocket();
      this.magneticToolState?.subscribeSocket();
    }
  }

  private unsubTools() {
    this.gravityToolState?.unsubscribeSocket();
    this.magneticToolState?.unsubscribeSocket();
  }

  private destroyTools() {
    this.gravityToolState?.destroy();
    this.magneticToolState?.destroy();
    this.gravityToolState = null;
    this.magneticToolState = null;
  }

  updateBaseParams() {
    if (this.fullyLoaded && this.wellbores.selected && this.baseParamsIssue) {
      void this.switchWellbore(this.wellbores.selected.id);
    }
  }

  destroy() {
    this.destroyed = true;
    this.abortController?.abort();
    this.throttledInit.cancel();
    this.disposerUnsub();
    this.clearSubTimeout();
    this.statePublisher.debouncedOnChange?.cancel();
    this.destroyTools();
    this.logs.destroy();
  }
}
